import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ServerPagination from '../components/ServerPagination';
import Filters from '../components/Filters';
import AccordionList from '../components/AccordionList';
import ExportButtons from '../components/ExportButtons';
import PageSizeSelector from '../components/PageSizeSelector';
import { getArtistInterviewsWithFilters, setFilters, resetFilters } from '../redux/ArtistInterviewSlice';
import { fetchArtistInterviewsWithFilters } from '../services/api';
import { handleExportResponse } from '../utils/exportUtils';
import RecordsLoader from '../components/RecordsLoader';
import ArtistInterviewModal from '../components/ArtistInterviewModal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ArtistInterviewsList = () => {
    const dispatch = useDispatch();
    const { artistInterviews, status, pagination, error } = useSelector((state) => state.artistInterviews);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [showFilters, setShowFilters] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedInterview, setSelectedInterview] = useState(null);

    const [localFilters, setLocalFilters] = useState({
        interview_title: '',
        interview_category: '',
        artist_title: '',
        slug: '',
        interview_artistic_fields: '',
        created_at_from: '',
        created_at_to: '',
        updated_at_from: '',
        updated_at_to: '',
    });

    // Filter configuration
    const filterConfig = [
        {
            key: 'interview_title',
            label: 'Interview Title',
            type: 'text',
            placeholder: 'Enter interview title',
        },
        {
            key: 'interview_category',
            label: 'Category',
            type: 'select',
            options: [
                { value: 'Artists Interviews', label: 'Artists Interviews' },
                { value: 'Artists Guides', label: 'Artists Guides' },
            ],
        },
        {
            key: 'artist_title',
            label: 'Artist Title',
            type: 'text',
            placeholder: 'Enter artist title',
        },
        {
            key: 'slug',
            label: 'Slug',
            type: 'text',
            placeholder: 'Enter slug',
        },
        {
            key: 'interview_artistic_fields',
            label: 'Artistic Fields',
            type: 'text',
            placeholder: 'Enter artistic fields',
        },
        {
            key: 'created_at_from',
            label: 'Created Date From',
            type: 'date',
        },
        {
            key: 'created_at_to',
            label: 'Created Date To',
            type: 'date',
        },
        {
            key: 'updated_at_from',
            label: 'Updated Date From',
            type: 'date',
        },
        {
            key: 'updated_at_to',
            label: 'Updated Date To',
            type: 'date',
        },
    ];

    // Fetch artist interviews with current filters and pagination
    const fetchArtistInterviews = useCallback(() => {
        const params = {
            page: currentPage,
            page_size: pageSize,
            ...localFilters,
        };
        
        // Remove empty filter values
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        dispatch(getArtistInterviewsWithFilters(params));
    }, [dispatch, currentPage, pageSize, localFilters]);

    useEffect(() => {
        fetchArtistInterviews();
    }, [fetchArtistInterviews]);

    const handleFilterChange = (filters) => {
        setLocalFilters(filters);
        dispatch(setFilters(filters));
        setCurrentPage(1);
    };

    const handleFilterReset = (filters) => {
        setLocalFilters(filters);
        dispatch(resetFilters());
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    const handleExport = async (format) => {
        try {
            const exportParams = { ...localFilters, export: format };
            
            Object.keys(exportParams).forEach(key => {
                if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
                    delete exportParams[key];
                }
            });

            const response = await fetchArtistInterviewsWithFilters(exportParams);
            handleExportResponse(response, format, `artist-interviews.${format}`);
        } catch (error) {
            console.error('Export error:', error);
            alert(error.message || 'Failed to export artist interviews. Please try again.');
        }
    };

    const handleEdit = (interview) => {
        setSelectedInterview(interview);
        setModalOpen(true);
    };

    const handleCreate = () => {
        setSelectedInterview(null);
        setModalOpen(true);
    };

    const handleSuccess = (message) => {
        toast.success(message);
        setModalOpen(false);
        fetchArtistInterviews(); // Refresh the list
    };

    return (
        <div className="container mx-auto p-6">
            <ToastContainer />
            {status === 'loading' && <RecordsLoader text="Loading Artist Interviews, please hold on..." />}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl text-black font-bold">Artist Interviews List</h1>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium flex items-center gap-2"
                    >
                        <svg
                            className={`w-5 h-5 transition-transform duration-200 ${showFilters ? 'transform rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </button>
                    <button
                        onClick={handleCreate}
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                    >
                        Create New Interview
                    </button>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="mb-4">
                    <Filters
                        filters={filterConfig}
                        onFilterChange={handleFilterChange}
                        onReset={handleFilterReset}
                    />
                </div>
            )}

            {/* Export Buttons */}
            <div className="mb-4">
                <ExportButtons
                    onExport={handleExport}
                    isLoading={status === 'loading'}
                />
            </div>

            {/* Page Size Selector */}
            <div className="mb-4">
                <PageSizeSelector
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {status === 'failed' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    Error loading Artist Interviews: {error || 'Unknown error'}
                </div>
            )}

            {/* Artist Interviews Accordion List */}
            {status === 'succeeded' && (
                <>
                    <AccordionList
                        data={artistInterviews || []}
                        getRowKey={(interview) => interview.id}
                        primaryFields={[
                            {
                                key: 'index',
                                label: '#',
                                render: (value, item, index) => {
                                    // Calculate descending index based on current page and page size
                                    const page = pagination?.page || currentPage;
                                    const pageSizeValue = pagination?.page_size || pageSize;
                                    const totalCount = pagination?.total_count || 0;
                                    const startIndex = (page - 1) * pageSizeValue;
                                    return totalCount - (startIndex + index);
                                },
                            },
                            {
                                key: 'interview_title',
                                label: 'Title',
                                flex: true,
                            },
                            {
                                key: 'interview_category',
                                label: 'Category',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'artist_title',
                                label: 'Artist',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'created_at',
                                label: 'Created',
                                render: (value) => formatDate(value),
                            },
                        ]}
                        expandedFields={[
                            {
                                key: 'slug',
                                label: 'Slug',
                                render: (value) => value || '—',
                                mono: true,
                            },
                            {
                                key: 'interview_artistic_fields',
                                label: 'Artistic Fields',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'interview_content',
                                label: 'Content',
                                render: (value) => {
                                    if (!value) return '—';
                                    // Strip HTML tags for display
                                    const div = document.createElement('div');
                                    div.innerHTML = value;
                                    const text = div.textContent || div.innerText || '';
                                    return text.length > 200 ? `${text.substring(0, 200)}...` : text;
                                },
                            },
                            {
                                key: 'artist_instagram',
                                label: 'Artist Instagram',
                                render: (value) => value ? (
                                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        {value}
                                    </a>
                                ) : '—',
                            },
                            {
                                key: 'artist_profile_url',
                                label: 'Artist Profile URL',
                                render: (value) => value ? (
                                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        {value}
                                    </a>
                                ) : '—',
                            },
                            {
                                key: 'artist_website',
                                label: 'Artist Website',
                                render: (value) => value ? (
                                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        {value}
                                    </a>
                                ) : '—',
                            },
                            {
                                key: 'gallery',
                                label: 'Gallery',
                                render: (value) => {
                                    if (!value || !Array.isArray(value) || value.length === 0) return '—';
                                    return `${value.length} image(s)`;
                                },
                            },
                            {
                                key: 'updated_at',
                                label: 'Updated At',
                                render: (value) => formatDate(value),
                            },
                        ]}
                        renderActions={(interview, isExpanded) => (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(interview);
                                }}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 transition-all duration-200 hover:scale-110 border border-blue-200"
                                title="Edit Interview"
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                </svg>
                            </button>
                        )}
                        emptyMessage="No artist interviews found!"
                        allowMultipleExpand={true}
                    />

                    {/* Server-side Pagination */}
                    {pagination && pagination.total_pages > 0 && (
                        <ServerPagination
                            currentPage={pagination.page || currentPage}
                            totalPages={pagination.total_pages || 1}
                            onPageChange={handlePageChange}
                            totalCount={pagination.total_count || 0}
                            pageSize={pagination.page_size || pageSize}
                            showPageInfo={true}
                        />
                    )}
                </>
            )}

            {/* Modal for Creating/Editing Interviews */}
            {modalOpen && (
                <ArtistInterviewModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    interviewData={selectedInterview}
                    onSuccess={handleSuccess}
                />
            )}
        </div>
    );
};

export default ArtistInterviewsList;
